import axios from 'axios';

export interface Partner {
  partnerId: string;
  partnerName: string;
  urlSlugName: string;
  role: string;
}

export interface SecurityInfo {
  username: string;
  authorities: string[];
  enabled: boolean;
  accountNonExpired: boolean;
  accountNonLocked: boolean;
  credentialsNonExpired: boolean;
  passwordExpired: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  user?: {
    partners: Partner[];
    securityInfo: SecurityInfo;
  };
}

export interface TokenInfo {
  token: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

export interface AuthState {
  isAuthenticated: boolean;
  user: {
    partners: Partner[];
    securityInfo: SecurityInfo;
  } | null;
  token: string | null;
  tokenExpiresAt: number | null;
  username: string | null;
  password: string | null;
  selectedPartner: Partner | null;
}

class AuthService {
  private static instance: AuthService;
  private authState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
    tokenExpiresAt: null,
    username: null,
    password: null,
    selectedPartner: null,
  };

  private constructor() {
    this.loadFromStorage();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private loadFromStorage(): void {
    try {
      const token = localStorage.getItem('auth_token');
      const tokenExpiresAt = localStorage.getItem('auth_token_expires_at');
      const userData = localStorage.getItem('auth_user');
      const selectedPartner = localStorage.getItem('selected_partner');
      const username = localStorage.getItem('auth_username');
      const password = localStorage.getItem('auth_password');

      if (token && userData) {
        // Check if token has expired
        const expiresAt = tokenExpiresAt ? parseInt(tokenExpiresAt) : null;
        const now = Date.now();
        
        if (expiresAt && now >= expiresAt) {
          // Token has expired, clear auth
          console.log('Token has expired, clearing authentication');
          this.clearAuth();
          return;
        }

        this.authState.token = token;
        this.authState.tokenExpiresAt = expiresAt;
        this.authState.user = JSON.parse(userData);
        this.authState.isAuthenticated = true;
        
        if (selectedPartner) {
          this.authState.selectedPartner = JSON.parse(selectedPartner);
        }
        
        if (username) {
          this.authState.username = username;
        }
        
        if (password) {
          this.authState.password = password;
        }
      }
    } catch (error) {
      console.error('Error loading auth data from storage:', error);
      this.clearAuth();
    }
  }

  private saveToStorage(): void {
    try {
      if (this.authState.token) {
        localStorage.setItem('auth_token', this.authState.token);
      }
      if (this.authState.tokenExpiresAt) {
        localStorage.setItem('auth_token_expires_at', this.authState.tokenExpiresAt.toString());
      }
      if (this.authState.user) {
        localStorage.setItem('auth_user', JSON.stringify(this.authState.user));
      }
      if (this.authState.selectedPartner) {
        localStorage.setItem('selected_partner', JSON.stringify(this.authState.selectedPartner));
      }
      if (this.authState.username) {
        localStorage.setItem('auth_username', this.authState.username);
      }
      if (this.authState.password) {
        localStorage.setItem('auth_password', this.authState.password);
      }
    } catch (error) {
      console.error('Error saving auth data to storage:', error);
    }
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      // Use same-origin by default so CRA proxy (dev) and server (prod) both work
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      const loginData = response.data;
      
      // Calculate token expiration time - extend to 8 hours for extended sessions
      const expiresIn = loginData.expiresIn || (8 * 60 * 60); // Default to 8 hours if not provided
      const expiresAt = Date.now() + (expiresIn * 1000);
      
      // Update auth state
      this.authState.isAuthenticated = true;
      this.authState.token = loginData.accessToken;
      this.authState.tokenExpiresAt = expiresAt;
      this.authState.user = loginData.user || null;
      this.authState.username = username;
      this.authState.password = password;
      
      // Auto-select first partner if available
      if (this.authState.user?.partners && this.authState.user.partners.length > 0) {
        this.authState.selectedPartner = this.authState.user.partners[0];
      }

      this.saveToStorage();
      return loginData;
    } catch (error) {
      throw error;
    }
  }

  logout(): void {
    this.clearAuth();
  }

  forceLogout(): void {
    this.clearAuth();
    // Redirect to login page if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private clearAuth(): void {
    this.authState = {
      isAuthenticated: false,
      user: null,
      token: null,
      tokenExpiresAt: null,
      username: null,
      password: null,
      selectedPartner: null,
    };
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token_expires_at');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('selected_partner');
    localStorage.removeItem('auth_username');
    localStorage.removeItem('auth_password');
  }

  isAuthenticated(): boolean {
    // Check if token has expired
    if (this.authState.tokenExpiresAt && Date.now() >= this.authState.tokenExpiresAt) {
      console.log('Token has expired, logging out');
      this.logout();
      return false;
    }
    return this.authState.isAuthenticated;
  }

  isTokenExpired(): boolean {
    return this.authState.tokenExpiresAt ? Date.now() >= this.authState.tokenExpiresAt : true;
  }

  getTokenExpirationTime(): number | null {
    return this.authState.tokenExpiresAt;
  }

  getSessionTimeRemaining(): string | null {
    if (!this.authState.tokenExpiresAt) {
      return null;
    }
    
    const now = Date.now();
    const expiresAt = this.authState.tokenExpiresAt;
    const remainingMs = expiresAt - now;
    
    if (remainingMs <= 0) {
      return 'Expired';
    }
    
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  hasRole(role: string): boolean {
    return this.authState.user?.securityInfo?.authorities?.includes(role) || false;
  }

  isSiteAdmin(): boolean {
    return this.hasRole('SITE_ADMIN');
  }

  getToken(): string | null {
    return this.authState.token;
  }

  getUser(): { partners: Partner[]; securityInfo: SecurityInfo } | null {
    return this.authState.user;
  }

  getPartners(): Partner[] {
    return this.authState.user?.partners || [];
  }

  getSelectedPartner(): Partner | null {
    return this.authState.selectedPartner;
  }

  setSelectedPartner(partner: Partner): void {
    this.authState.selectedPartner = partner;
    this.saveToStorage();
  }

  getAuthState(): AuthState {
    return { ...this.authState };
  }
}

export default AuthService;
